import React, { useState } from 'react';
import { Sparkles, Wand2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  applyHtmlToComposer,
  buildIncomingEmailPayload,
  plainTextToQuillHtml,
  requestEmailAssist,
  stripHtmlToPlain,
} from '../utils/emailAiAssist';

const waitForComposerMount = () =>
  new Promise((resolve) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });

const EmailComposerAiButtons = ({
  composerMode,
  sourceEmail,
  getDraftHtml,
  composerHtmlRef,
  setComposerData,
  setComposerEditorKey,
  disabled = false,
  className = '',
  compact = false,
}) => {
  const [loadingMode, setLoadingMode] = useState(null);

  const canDraftReply =
    sourceEmail &&
    (composerMode === 'reply' || composerMode === 'replyAll');

  const getDraftPlain = () => {
    const html = typeof getDraftHtml === 'function' ? getDraftHtml() : '';
    return stripHtmlToPlain(html);
  };

  const applyText = (text) => {
    applyHtmlToComposer({
      html: plainTextToQuillHtml(text),
      composerHtmlRef,
      setComposerData,
      setComposerEditorKey,
    });
  };

  const runAssist = async (mode, { waitMount = false } = {}) => {
    if (disabled || loadingMode) return;
    setLoadingMode(mode);
    try {
      if (waitMount) await waitForComposerMount();

      if (mode === 'draft_reply') {
        const incoming = buildIncomingEmailPayload(sourceEmail);
        if (!incoming?.body && !incoming?.subject) {
          toast.error('Keine Originalnachricht zum Antworten');
          return;
        }
        const text = await requestEmailAssist({ mode: 'draft_reply', incoming });
        applyText(text);
        toast.success('Antwortentwurf eingefügt — bitte prüfen und senden');
        return;
      }

      const draft = getDraftPlain();
      if (!draft || draft.length < 2) {
        toast.error('Bitte zuerst einen Text schreiben');
        return;
      }
      const text = await requestEmailAssist({
        mode: 'improve',
        draft: draft.slice(0, 8000),
        incoming: buildIncomingEmailPayload(sourceEmail) || undefined,
      });
      applyText(text);
      toast.success('Text verbessert — bitte prüfen');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'KI-Hilfe fehlgeschlagen');
    } finally {
      setLoadingMode(null);
    }
  };

  const btnClass = compact
    ? 'inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-50'
    : 'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 bg-primary-50/80 dark:bg-primary-950/40 hover:bg-primary-100 dark:hover:bg-primary-900/50 disabled:opacity-50';

  const improveDisabled = disabled || Boolean(loadingMode);

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`.trim()}>
      {canDraftReply ? (
        <button
          type="button"
          disabled={disabled || Boolean(loadingMode)}
          onClick={() => runAssist('draft_reply')}
          className={btnClass}
          title="KI schlägt eine Antwort vor — Sie können sie bearbeiten und absenden"
        >
          {loadingMode === 'draft_reply' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Sparkles className="w-3.5 h-3.5" />
          )}
          Antwort vorschlagen
        </button>
      ) : null}
      <button
        type="button"
        disabled={improveDisabled}
        onClick={() => runAssist('improve')}
        className={btnClass}
        title="Grammatik, Stil und Klarheit Ihres Entwurfs verbessern"
      >
        {loadingMode === 'improve' ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Wand2 className="w-3.5 h-3.5" />
        )}
        Text verbessern
      </button>
    </div>
  );
};

export default EmailComposerAiButtons;
